import PropTypes from 'prop-types'
import { createContext, forwardRef, memo, useContext, useMemo } from 'react'
import useFallbackRef from 'lib/react/hooks/useFallbackRef'
import classNames from 'lib/util/classNames'
import useIntersectionObserver from '../Intersection/hooks/useIntersectionObserver'
import Author from './Author/Author'
import Block from './Block/Block'
import Content from './Content/Content'
import Description from './Description/Description'
import Footer from './Footer/Footer'
import Header from './Header/Header'
import Label from './Label/Label'
import styles from './LineItem.module.scss'
import Media from './Media/Media'
import Options from './Options/Options'
import Subtitle from './Subtitle/Subtitle'
import Symbol from './Symbol/Symbol'
import Tag from './Tag/Tag'
import Tags from './Tags/Tags'
import Title from './Title/Title'
import Value from './Value/Value'

// Create the context
const LineItemContext = createContext({
  isIntersecting: false
})

// Export the context as a hook for quick usage
export const useLineItemContext = () => {
  const context = useContext(LineItemContext)

  return context
}

/**
 * The `LineItem`
 *
 * @param {object} props - the component props
 * @returns {React.ReactElement} the element
 */
const LineItemComponent = forwardRef((props, forwardedRef) => {
  const { Tag, children, className, gridGap, ...rest } = props
  const ref = useFallbackRef(forwardedRef)
  const { isIntersecting } = useIntersectionObserver(ref)
  const classNameOutput = useMemo(
    () =>
      classNames([
        className,
        styles.container,
        isIntersecting && styles.intersecting,
        styles[`grid-gap-${gridGap}`]
      ]),
    [className, gridGap]
  )

  const ctx = useMemo(
    () => ({
      isIntersecting
    }),
    [isIntersecting]
  )

  return (
    <LineItemContext.Provider value={ctx}>
      <Tag ref={ref} className={classNameOutput} {...rest}>
        <div className={styles.display}>{children}</div>
      </Tag>
    </LineItemContext.Provider>
  )
})

// Display name
LineItemComponent.displayName = 'LineItem'

/** @type {object} */
LineItemComponent.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  Tag: PropTypes.elementType,
  gridGap: PropTypes.string
}

/** @type {object} */
LineItemComponent.defaultProps = {
  children: null,
  className: '',
  Tag: 'div',
  gridGap: 'standard'
}

// Memoize
const LineItem = memo(LineItemComponent)

// Bind the types
LineItem.Author = Author
LineItem.Block = Block
LineItem.Content = Content
LineItem.Footer = Footer
LineItem.Header = Header
LineItem.Label = Label
LineItem.Media = Media
LineItem.Options = Options
LineItem.Symbol = Symbol
LineItem.Tag = Tag
LineItem.Tags = Tags
LineItem.Title = Title
LineItem.Value = Value
LineItem.Description = Description
LineItem.Subtitle = Subtitle

// Export
export default LineItem
